import React from 'react';
import { useRentingReports } from '../hooks/useCarReports';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/ui/button/Button';
import { ReportsListResponse, carReportsApi } from '../api/carReportsApi';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DetailsCard from '@/components/common/DetailsCard';

interface CarReportsListProps {
  rentingId: string;
  carId: string;
  rentingStatus: 'draft' | 'confirmed' | 'cancelled';
}

const CarReportsList: React.FC<CarReportsListProps> = ({ rentingId, carId, rentingStatus }) => {
  const { data: reports, isLoading, error } = useRentingReports(rentingId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateReportLoading, setIsCreateReportLoading] = React.useState(false);
  const [loadingEditReportIds, setLoadingEditReportIds] = React.useState<Record<string, boolean>>({});
  const { t } = useTranslation();

  // Сортируем отчеты так, чтобы delivery report был первым
  const sortedReports = React.useMemo(() => {
    if (!reports) return [];
    return [...reports].sort((a, b) => {
      if (a.activity === 'delivery_to_client') return -1;
      if (b.activity === 'delivery_to_client') return 1;
      return 0;
    });
  }, [reports]);

  const handleEditClick = async (report: ReportsListResponse['data'][0]) => {
    // Устанавливаем состояние загрузки для этого отчета
    setLoadingEditReportIds(prev => ({ ...prev, [report.id]: true }));

    try {
      // Предварительно загружаем технический профиль автомобиля
      await queryClient.fetchQuery({
        queryKey: ['car-tech-profile', carId],
        queryFn: () => carReportsApi.getTechProfile(carId)
      });

      // Предварительно загружаем данные отчета
      await queryClient.fetchQuery({
        queryKey: ['report', report.id],
        queryFn: () => carReportsApi.getReport(report.id)
      });

      // Переходим на страницу редактирования
      navigate(`/rentings/${rentingId}/reports/${report.id}/edit`);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      // Сбрасываем состояние загрузки для этого отчета
      setLoadingEditReportIds(prev => ({ ...prev, [report.id]: false }));
    }
  };

  // Проверяем, какие типы отчетов уже существуют
  const existingReportTypes = React.useMemo(() => {
    if (!reports) return new Set<string>();
    return new Set(reports.map(report => report.activity));
  }, [reports]);

  // Определяем доступные типы отчетов
  const availableReportTypes = React.useMemo(() => [
    { value: 'delivery_to_client', label: t("Delivery Report"), disabled: existingReportTypes.has('delivery_to_client') },
    { value: 'pickup_from_client', label: t("Pickup Report"), disabled: existingReportTypes.has('pickup_from_client') }
  ], [existingReportTypes, t]);

  // Проверяем, можно ли создать новый отчет
  const canCreateReport = rentingStatus !== 'cancelled' && availableReportTypes.some(type => !type.disabled);

  const handleCreateReportClick = async () => {
    setIsCreateReportLoading(true);
    try {
      // Предварительно загружаем технический профиль автомобиля
      await queryClient.fetchQuery({
        queryKey: ['car-tech-profile', carId],
        queryFn: () => carReportsApi.getTechProfile(carId)
      });

      // Предварительно загружаем отчеты для этой аренды
      await queryClient.fetchQuery({
        queryKey: ['reports', rentingId],
        queryFn: async () => {
          const response = await carReportsApi.getReports();
          return response.data.filter(report => report.renting.id === rentingId);
        }
      });

      // Переходим на страницу создания репорта
      navigate(`/rentings/${rentingId}/reports/new`);
    } catch (error) {
      console.error('Failed to load data for report creation:', error);
    } finally {
      setIsCreateReportLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border-primary bg-bg-card p-4 md:p-6 xl:p-7.5">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-border-primary bg-bg-card p-4 md:p-6 xl:p-7.5">
        <div className="text-red-500">{t("Failed to load reports")}</div>
      </div>
    );
  }

  return (
    <>
    <DetailsCard
      title={t("Car Reports")}
    >
      <div className="space-y-4">
        {canCreateReport && (
            <Button
              variant="primary"
              testId="create-report-button"
              size="sm"
              onClick={handleCreateReportClick}
              disabled={isCreateReportLoading}
              className="mb-4"
            >
              {isCreateReportLoading ? (
                <span className="flex items-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  {t("Loading...")}
                </span>
              ) : (
                t("Create Report")
              )}
            </Button>
          )}
        </div>
        <div className="space-y-4">
          {sortedReports.length ? (
            sortedReports.map((report) => (
              <div
                key={report.id}
                data-testid={`car-report-item-${report.id}`}
                className="border border-border-primary rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <Link
                      to={`/reports/${report.id}`}
                      className="font-medium text-text-primary hover:underline"
                    >
                      {report.activity === 'delivery_to_client'
                        ? t("Delivery Report")
                        : t("Pickup Report")}
                    </Link>
                    <div className="text-sm text-text-tertiary">
                      {format(new Date(report.created_at), 'PPp')}
                    </div>
                  </div>
                  <Button
                    testId={`edit-report-button-${report.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditClick(report)}
                    disabled={loadingEditReportIds[report.id]}
                  >
                    {loadingEditReportIds[report.id] ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></span>
                        {t("Loading...")}
                      </span>
                    ) : (
                      t("Edit")
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <div className="text-sm text-text-tertiary">{t("Mileage")}</div>
                    <div className="font-medium text-text-primary">{report.mileage} km</div>
                  </div>
                  <div>
                    <div className="text-sm text-text-tertiary">{t("Fuel Level")}</div>
                    <div className="font-medium text-text-primary">{report.fuel_level}%</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-text-tertiary">{t("No reports available")}</div>
          )}
        </div>
    </DetailsCard>
    </>
  );
};

export default CarReportsList;
